import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'

import {
  HOVER_DIMMED_OPACITY,
  HOVER_FOCUS_TRANSITION_CSS,
  HOVER_FOCUSED_OPACITY,
  HOVER_TRANSITION_CSS,
} from 'features/charts-v2/chart-hover-constants'
import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import { barMinPointSize } from 'features/charts-v2/constants'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import {
  COMPARISON_SERIES_COLOR,
  DEFAULT_SERIES_COLOR,
} from 'features/charts-v2/services/series-color-palette' // bully ignore[shared-imports-features: applies predicate is always_ts_file (tools/bully/policy/src/rules/rules/shared_imports_features/mod.rs:19), not scoped to configured shared_layer_paths, so it fires on every intra-feature import in this file. follow-up: .agents/follow-ups/bully-typescript-false-positive-rules.md]
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import {
  quantizeBarValuesToDisplayPrecision,
  resolveBarValueDomain,
} from 'features/charts-v2/utils/axis-calculations'
import {
  calculateAvailableXAxisWidth,
  calculateXAxisHeightFromLabels,
  formatAxisNumber,
  getMetricAxisDomain,
  OVERFLOW_CONFIG,
} from 'features/charts-v2/utils/axis-tick-utils'
import { CustomXAxisTick } from 'features/charts-v2/utils/AxisTickUtils'
import {
  companionVolumeRow,
  countMetricRow,
  metricRow,
} from 'features/charts-v2/utils/build-hover-annotation'

/**
 * Format number for display in labels
 * - Whole numbers: no decimals (e.g., 5)
 * - Decimals: max 2 decimal places (e.g., 4.91, 3.5)
 */
const formatLabelValue = (value: number): string => {
  if (Number.isInteger(value)) {
    return value.toString()
  }
  // Round to 2 decimal places and remove trailing zeros
  return parseFloat(value.toFixed(2)).toString()
}

/**
 * Custom label component for bar data points
 * Displays the value above each bar
 */
const CustomBarLabel = React.memo<{
  x?: number
  y?: number
  width?: number
  height?: number
  value?: number
  fontSizePx?: number
}>(({ x = 0, y = 0, width = 0, value, fontSizePx = 13 }) => {
  // Don't render label if value is null/undefined/0
  if (value == null || value === 0) return null

  return (
    <text
      x={x + width / 2}
      y={y - 5}
      fill="#333"
      fontSize={fontSizePx}
      textAnchor="middle"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {formatLabelValue(value)}
    </text>
  )
})

CustomBarLabel.displayName = 'CustomBarLabel'

/**
 * Custom label component for line data points
 * Displays the value above each point on the line
 */
const CustomLineLabel = React.memo<{
  x?: number
  y?: number
  value?: number
  fontSizePx?: number
}>(({ x = 0, y = 0, value, fontSizePx = 13 }) => {
  // Don't render label if value is null/undefined/0
  if (value == null || value === 0) return null

  return (
    <text
      x={x}
      y={y - 10}
      fill={COMPARISON_SERIES_COLOR}
      fontSize={fontSizePx}
      textAnchor="middle"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {formatLabelValue(value)}
    </text>
  )
})

CustomLineLabel.displayName = 'CustomLineLabel'

// Recharts mouse event type for bar chart
interface RechartsBarChartMouseEvent {
  activeLabel?: string
  activePayload?: Array<{
    value: number
    dataKey: string
    payload: Record<string, unknown>
  }>
}

// Bar click data type
interface BarClickData {
  payload?: Record<string, unknown>
}

/**
 * Styled container that handles hover effects with CSS to avoid React re-renders
 */
const ChartContainer = styled.div`
  width: 100%;
  height: 100%;

  /* When hovering over any bar, dim all bars */
  &:hover .recharts-bar-rectangle {
    opacity: ${HOVER_DIMMED_OPACITY};
    transition: ${HOVER_TRANSITION_CSS};
  }

  /* When hovering over a specific bar, highlight only that one */
  &:hover .recharts-bar-rectangle:hover {
    opacity: ${HOVER_FOCUSED_OPACITY} !important;
    transition: ${HOVER_FOCUS_TRANSITION_CSS};
  }
`

export class BarChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.BAR_VERTICAL
  public readonly requiredMetrics: MetricType[] = []
  public readonly optionalMetrics: MetricType[] = []

  public transform(props: ChartProps): TransformedChartData {
    const breakdowns = props.selectedBreakdowns || []

    // Handle 0 breakdowns: show single bar with total
    if (breakdowns.length === 0) {
      return quantizeBarValuesToDisplayPrecision(
        dataTransformationService.transformForTotalOnly(
          props.data!,
          props.selectedMetrics,
        ),
        props.selectedMetrics,
      )
    }

    // Use dual-axis transformation if multiple metrics are selected
    if (props.selectedMetrics.length > 1) {
      return quantizeBarValuesToDisplayPrecision(
        dataTransformationService.transformForDualAxisChart(
          props.data!,
          props.selectedMetrics,
          props.timeBucket,
        ),
        props.selectedMetrics,
      )
    }

    // Single metric uses basic transformation
    return quantizeBarValuesToDisplayPrecision(
      dataTransformationService.transformForBasicChart(
        props.data!,
        props.selectedMetrics,
        props.timeBucket,
        props.sortDirection,
      ),
      props.selectedMetrics,
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      transformedData,
      onDataPointHover,
      selectedMetrics,
      onSegmentClick,
      fontSizePx = 13,
      showXAxisLabel = true,
      showYAxisLabel = true,
      showDatapointLabels = true,
      xAxisOverflow = 'expand',
      yAxisDomain,
      disableAnimation = false,
    } = props

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    // Check if this is a dual-axis chart (multiple metrics)
    const isDualAxis = selectedMetrics.length > 1

    const primaryMetric = selectedMetrics[0]!
    const isVolumeMetric =
      primaryMetric === 'rating' || primaryMetric === 'sentiment'
    const isCountMetric =
      primaryMetric === 'record_count' || primaryMetric === 'sentence_count'

    // Compute total across all bars for percentage display (single-metric only)
    let tooltipTotal = 0
    if (!isDualAxis) {
      if (isCountMetric) {
        tooltipTotal = transformedData.chartData.reduce(
          (sum, d) => sum + ((d['count'] as number) || 0),
          0,
        )
      } else if (isVolumeMetric) {
        tooltipTotal = transformedData.chartData.reduce(
          (sum, d) => sum + ((d['volumeCount'] as number) || 0),
          0,
        )
      }
    }

    // Calculate dynamic maxWidth for wrap mode based on data length
    // Estimate: assume chart is ~1200px wide (typical for full-width charts)
    const estimatedChartWidth = 1200
    const maxWidthPerLabel =
      xAxisOverflow === 'wrap'
        ? calculateAvailableXAxisWidth(
            estimatedChartWidth,
            transformedData.chartData.length,
            { left: 40, right: 30 },
          )
        : 100

    const tickRenderer = (props: {
      x: number
      y: number
      payload: { value: string }
      [k: string]: unknown
    }): JSX.Element => (
      <CustomXAxisTick
        {...props}
        fontSizePx={fontSizePx}
        overflowMode={xAxisOverflow}
        maxWidth={maxWidthPerLabel}
      />
    )

    // Extract labels from chart data
    const labels = transformedData.chartData.map((d) => String(d['name'] ?? ''))

    // Calculate x-axis height dynamically based on overflow mode
    let xAxisHeight: number
    let bottomMargin: number

    if (xAxisOverflow === 'expand') {
      // Calculate based on actual text width for angled labels
      xAxisHeight = calculateXAxisHeightFromLabels(labels, fontSizePx)
      bottomMargin = 20
    } else {
      // Use fixed configuration for wrap and truncate modes
      const overflowConfig = OVERFLOW_CONFIG[xAxisOverflow]
      xAxisHeight = overflowConfig.height
      bottomMargin = overflowConfig.margin
    }

    return (
      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            key={`chart-${yAxisDomain?.[1] ?? 'auto'}`}
            data={transformedData.chartData}
            margin={{ ...this.getCommonMargin(), bottom: bottomMargin }}
            onMouseMove={(data: RechartsBarChartMouseEvent) => {
              if (data && data.activeLabel) {
                onDataPointHover?.(data.activeLabel)
              }
            }}
            onMouseLeave={() => onDataPointHover?.(null)}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              height={xAxisHeight}
              interval={0}
              tick={showXAxisLabel ? tickRenderer : false}
            />

            {/* Dual-axis: left Y-axis for bar, right Y-axis for line */}
            {isDualAxis ? (
              <>
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  stroke={DEFAULT_SERIES_COLOR}
                  tick={{ fontSize: fontSizePx }}
                  tickFormatter={formatAxisNumber}
                  domain={undefined}
                  label={
                    showYAxisLabel
                      ? {
                          value: this.getMetricLabel([selectedMetrics[0]!]),
                          angle: -90,
                          position: 'insideLeft',
                          style: {
                            textAnchor: 'middle',
                            fill: DEFAULT_SERIES_COLOR,
                            fontSize: fontSizePx,
                          },
                        }
                      : undefined
                  }
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke={COMPARISON_SERIES_COLOR}
                  tick={{ fontSize: fontSizePx }}
                  tickFormatter={formatAxisNumber}
                  domain={undefined}
                  label={
                    showYAxisLabel
                      ? {
                          value: this.getMetricLabel([selectedMetrics[1]!]),
                          angle: 90,
                          position: 'insideRight',
                          style: {
                            textAnchor: 'middle',
                            fill: COMPARISON_SERIES_COLOR,
                            fontSize: fontSizePx,
                          },
                        }
                      : undefined
                  }
                />
              </>
            ) : (
              <YAxis
                tick={{ fontSize: fontSizePx }}
                tickFormatter={formatAxisNumber}
                domain={resolveBarValueDomain(
                  yAxisDomain || getMetricAxisDomain(selectedMetrics[0]!),
                  selectedMetrics[0],
                )}
                label={
                  showYAxisLabel
                    ? {
                        value: this.getMetricLabel([selectedMetrics[0]!]),
                        angle: -90,
                        position: 'insideLeft',
                        style: {
                          textAnchor: 'middle',
                          fill: '#666',
                          fontSize: fontSizePx,
                        },
                      }
                    : undefined
                }
              />
            )}

            <Tooltip
              content={({
                active,
                payload: tooltipPayload,
                label: tooltipLabel,
              }) => {
                if (!active || !tooltipPayload?.length) return null
                const rows = tooltipPayload.flatMap((entry) => {
                  const val = entry.value as number
                  const dp = entry.payload as Record<string, unknown>
                  if (isDualAxis) {
                    const metricIndex = entry.dataKey === 'metric1' ? 0 : 1
                    return [metricRow(selectedMetrics[metricIndex]!, val)]
                  } else if (isVolumeMetric) {
                    const volumeCount = dp['volumeCount'] as number | undefined
                    return [
                      metricRow(primaryMetric, val),
                      ...(companionVolumeRow(
                        primaryMetric,
                        volumeCount,
                        tooltipTotal,
                      )
                        ? [
                            companionVolumeRow(
                              primaryMetric,
                              volumeCount,
                              tooltipTotal,
                            )!,
                          ]
                        : []),
                    ]
                  } else if (isCountMetric) {
                    return [countMetricRow(primaryMetric, val, tooltipTotal)]
                  } else {
                    return [metricRow(primaryMetric, val)]
                  }
                })
                return (
                  <ChartDatapointHoverAnnotation
                    title={String(tooltipLabel)}
                    rows={rows}
                    footer={null}
                    focusedField={null}
                  />
                )
              }}
            />
            {/* Only show legend for dual-axis charts */}
            {isDualAxis && (
              <Legend
                formatter={(value: string) => {
                  if (value === 'metric1')
                    return this.getMetricLabel([selectedMetrics[0]!])
                  if (value === 'metric2')
                    return this.getMetricLabel([selectedMetrics[1]!])
                  return value
                }}
                wrapperStyle={{ fontSize: fontSizePx }}
              />
            )}

            {/* Render bar for first metric */}
            <Bar
              dataKey={isDualAxis ? 'metric1' : 'count'}
              name={
                isDualAxis ? 'metric1' : this.getMetricLabel(selectedMetrics)
              }
              yAxisId={isDualAxis ? 'left' : undefined}
              fill={DEFAULT_SERIES_COLOR}
              radius={[4, 4, 0, 0]}
              minPointSize={barMinPointSize}
              isAnimationActive={!showDatapointLabels && !disableAnimation}
              label={
                showDatapointLabels
                  ? (props: Record<string, unknown>) => (
                      <CustomBarLabel {...props} fontSizePx={fontSizePx} />
                    )
                  : false
              }
              onClick={(
                data: BarClickData,
                _index: number,
                event: React.MouseEvent,
              ) => {
                if (onSegmentClick && data && data.payload) {
                  const valueKey = isDualAxis ? 'metric1' : 'count'
                  onSegmentClick(
                    {
                      primaryBreakdown: data.payload['name'] as string,
                      value: data.payload[valueKey] as number,
                      payload: data.payload as Record<
                        string,
                        string | number | boolean | null
                      >,
                    },
                    event,
                  )
                }
              }}
            />

            {/* Render line for second metric (dual-axis only) */}
            {isDualAxis && (
              <Line
                type="monotone"
                dataKey="metric2"
                name="metric2"
                stroke={COMPARISON_SERIES_COLOR}
                strokeWidth={2}
                yAxisId="right"
                dot={{ fill: COMPARISON_SERIES_COLOR, r: 4 }}
                activeDot={{ r: 6 }}
                isAnimationActive={!showDatapointLabels && !disableAnimation}
                label={
                  showDatapointLabels
                    ? (props: Record<string, unknown>) => (
                        <CustomLineLabel {...props} fontSizePx={fontSizePx} />
                      )
                    : false
                }
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    )
  }

  public getTitle(): string {
    return 'Posts by Category'
  }
}
